/**
 * Form validation utilities
 */

// Mirrors the backend check (services/workforce/validation.py, which delegates to
// email_validator). The previous pattern here was /^[^\s@]+@[^\s@]+\.[^\s@]+$/, which
// only ruled out spaces and stray @ — it happily accepted "vatkins@gjauto.com>", the
// pasted-mail-header typo that made a real NADA account impossible to log into or
// reset, and equally accepted "a@b,com" and "a@b..com".
//
// Local part: dot-separated atoms, so no leading/trailing dot and no "..".
// Domain: dot-separated labels that neither start nor end in a hyphen, with an
// alphabetic TLD of at least two characters — "a@localhost" is not deliverable here.
// Plus-addressing is inside the allowed atom set on purpose: real accounts sign in as
// "someone+1@example.com" and must keep working.
const LOCAL_ATOM = "[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+";
const DOMAIN_LABEL = "[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?";
const EMAIL_REGEX = new RegExp(
    `^${LOCAL_ATOM}(?:\\.${LOCAL_ATOM})*@(?:${DOMAIN_LABEL}\\.)+[A-Za-z]{2,}$`
);

export const validateEmail = (email: string): boolean => {
    // Trim rather than reject: addresses arrive pasted, and the backend trims too.
    return EMAIL_REGEX.test(email.trim());
};

/** Storage/transport form of an address: what the API should receive once it validates. */
export const normalizeEmail = (email: string): string => email.trim();

export const validatePhone = (phone: string): boolean => {
    // Allow various phone formats: (123) 456-7890, 123-456-7890, 1234567890, etc.
    const phoneRegex = /^[\d\s\-\(\)]+$/;
    const digitsOnly = phone.replace(/\D/g, "");
    return phoneRegex.test(phone) && digitsOnly.length >= 10;
};

export const validateQuebecPostalCode = (postalCode: string): boolean => {
    // Quebec postal code format: A1A 1A1
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
    return postalCodeRegex.test(postalCode);
};

export const validateRequired = (value: string): boolean => {
    return value.trim().length > 0;
};

export const getEmailError = (email: string, touched: boolean): string => {
    if (!touched) return "";
    if (!validateRequired(email)) return "Email is required";
    if (!validateEmail(email)) return "Please enter a valid email address";
    return "";
};

export const getPhoneError = (phone: string, touched: boolean): string => {
    if (!touched) return "";
    if (!validateRequired(phone)) return "Phone number is required";
    if (!validatePhone(phone)) return "Please enter a valid phone number (at least 10 digits)";
    return "";
};

export const getRequiredError = (value: string, touched: boolean, fieldName: string): string => {
    if (!touched) return "";
    if (!validateRequired(value)) return `${fieldName} is required`;
    return "";
};
